import StringUtil from "core/util/StringUtil";
import { IRenderProgramTrackProps, ProgramQueryEager, ProgramQueryLazy, TrackProductEager } from "./ProgramQueries";
import React, { useState } from "react";
import { BlockView } from "common/views/block/BlockView.view";
import { ImageView } from "common/views/image/Image.view";
import HeadingView from "common/views/heading/Heading.view";
import Label from "core/components/label/Label";
import TypographyView from "common/views/typographyView/Typography.view";
import ProgressBarView from "common/views/progressBar/ProgressBar.view";
import { ButtonView } from "common/views/button/Button.view";
import { Collapse } from "@blueprintjs/core";
import { RenderTrackItemsEager, RenderTrackItemsLazy } from "./TrackItem";
import { ViewProvider } from "core/decorators/View";
import Query from "core/api/QueryComponent";
import fliter from "./ProgramFliter"

export const RenderProgramTrackEager = (props: IRenderProgramTrackProps) => {
	//state in fuction component for toggling collapase-> react 16+ feature
    const { courseFramentPops, courseID } = props;
    const { course, searchText } = courseFramentPops;

    return (
			<Query.Root
				entity={ProgramQueryEager}
				args={{
					id: courseID,
					language: course.language,
					query: !( props.courseFramentPops.searchIncludesTag || false ) ? searchText: "",
				}}
			>
				{({ data }) => {
                    let program = (data && data.program);
            
					if (
						!program ||
						!program.tracks ||
						program.tracks.length < 1
					) {
						return null;
					}

                    const searchIncludesTag = props.courseFramentPops.searchIncludesTag || false;
                    program = searchIncludesTag? fliter(data.program , searchText) : data.program;

                    return program.tracks.map((track) => {
                        let pageNumber = getPageNumber(
                            courseFramentPops.trackPaginationVal,
                            track
                        );
                    
                        let product =  program.tracks.find(i => i.id === track.id).products;

                        if(searchText && !hasAtLeastItem(product)) return null;
                       
                        return (
                            <BlockView
                            viewKey="program.track"
                            key={track.id}
                            style={{ display: "flex", justifyContent: "space-between" }}
                        >
                             <div style={{ flexGrow: 1 }}>
                                <RenderTrack track={track} />
                                <RenderTrackItemsEager
                                    courseFramentPops={courseFramentPops}
                                    products={product}
                                    track={track}
                                    pageNumber={pageNumber}
                                />
                            </div>
                        </BlockView>
                        );
                    });
				}}
			</Query.Root>
	);

};

export const RenderProgramTrackLazy = (props: IRenderProgramTrackProps) => {
    const { courseFramentPops, courseID } = props;
    const { course } = courseFramentPops;

    return (
        <ViewProvider keyPrefix="overview.">
            <Query.Root
                entity={ProgramQueryLazy}
                args={{ id: courseID, language: course.language }}
            >
                {({ data }) => {
                    const program = data && data.program;

                    if (
                        !program ||
                        !program.tracks ||
                        program.tracks.length < 1
                    ) {
                        return null;
                    }
                     //for lazy load
                     let totaltracks = getTotal(program);

                     if(totaltracks <= courseFramentPops.lazyLoadSize) {
                            return <RenderProgramTrackEager courseFramentPops={props.courseFramentPops} courseID= {props.courseID}/>
                     }

                    return program.tracks.map((track) => {
                        let pageNumber = getPageNumber(
                            courseFramentPops.trackPaginationVal,
                            track
                        );
                       
                        return (
                            <BlockView
                                viewKey="program.track"
                                key={track.id}
                                style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                }}
                            >
                                <div style={{ flexGrow: 1 }}>
                                    <RenderTrack track={track} />
                                    <ItemLazy courseFramentPops={courseFramentPops} track={track} pageNumber={pageNumber} />
                                </div>
                            </BlockView>
                        );
                    });
                }}
            </Query.Root>
        </ViewProvider>
    );
};

const ItemLazy = ({ courseFramentPops, track, pageNumber }) => {
    const [isOpen, setToggleCollapse] = useState(false);
    return (
        <React.Fragment>
            <ButtonView
                viewKey="program.button"
                onClick={() => setToggleCollapse(!isOpen)}
                style={{
                    transform: isOpen ? `rotate(180deg)` : "rotate(0deg)",
                }}
            />
            <Collapse isOpen={isOpen}>
                {
                    <RenderTrackItemsLazy
                        courseFramentPops={courseFramentPops}
                        track={track}
                        pageNumber={pageNumber}
                    />
                }
            </Collapse>
        </React.Fragment>
    );
};

const RenderTrack = ({ track }) => {
    return (
        <React.Fragment>
            {track.iconUrl && (
                <ImageView viewKey="program.track.icon" src={track.iconUrl} />
            )}
            {
                <div style={{ flexGrow: 1 }}>
                    <HeadingView viewKey="program.track.name">
                        <Label
                            k={`ProgramTrack.${track.id}.name`}
                            v={track.name}
                        />
                    </HeadingView>
                    {track.description && (
                        <div
                            className="html-default"
                            style={{ marginTop: "10px" }}
                        >
                            <TypographyView
                                viewKey="program.track.description"
                                html={track.description}
                            />
                        </div>
                    )}

                    {track.status &&
                        track.status.map(({ completed, total, type }) => (
                            <ProgressBarView
                                viewKey="program.track.progressbar"
                                key={type}
                                style={{ marginTop: "10px" }}
                                value={total > 0 ? completed / total : 0}
                                label={
                                    <TypographyView viewKey="program.track.progressbar.label">
                                        <Label
                                            k={`program.track.requirement.${type}`}
                                            v={
                                                "${completed} / ${total} " +
                                                type
                                            }
                                            args={{
                                                completed,
                                                total,
                                            }}
                                        />
                                    </TypographyView>
                                }
                            />
                        ))}
                </div>
            }
        </React.Fragment>
    );
};

function getPageNumber(trackPaginationVal, track) {
    const foundPage = trackPaginationVal.find((page) => track.id === page.trackId);
    return foundPage ? StringUtil.toInteger(foundPage.pageNumber) : 0;
}

function getTotal(program) {
    let totaltracks = 0;
    if (program && program.tracks) {
        program.tracks.forEach((data) => {
            if (data.products && data.products.length > 0) {
                totaltracks += 1;
            }
        });

        return totaltracks;
    }


}

function hasAtLeastItem(products: TrackProductEager[]){
    return products && products.find(product => product.course && product.course != null)
}


// WEBPACK FOOTER //
// ./src/panels/catalog/fragments/overview/ProgramTrack.tsx